import {
  Field,
  Label,
  Select,
  EXPERIMENTAL_Suggestion as Suggestion,
} from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormWrapper, TranslatedValidationMessage } from "@/components";
import { useAuth } from "@/features/auth";
import {
  type Aktoer,
  type BestillingAktoer,
  BestillingFormErrorSummary,
  type BestillingPageKey,
  useBestillingContext,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
  useBestillingNavigation,
} from "@/features/navigation";
import { isDirty } from "@/utils";
import { formatPid } from "@/utils/formatPid";
import {
  type AktoerSelected,
  aktoerFormSchema,
  type aktoerFormValues,
  convertSelctedToForm,
  formatAktoerNavn,
  getDefaultAktoerFormValues,
  useFromToSelected,
} from "./AktoerFormSchemas";

type AktoerFormProps = {
  aktoerer: Aktoer[];
};
const currentPageKey: BestillingPageKey = "/bestilling/ny/aktoer";

export function AktoerForm({ aktoerer }: AktoerFormProps) {
  const { t } = useTranslation();
  const { account } = useAuth();

  const { navigateNext } = useBestillingNavigation(currentPageKey);
  const { readOnly, bestilling, setBestillingData } = useBestillingContext();

  const mySelfSelect: AktoerSelected = {
    label: `${account?.navn} (${formatPid(account?.pid)})`,
    value: account?.pid || "",
  };

  const form = useForm({
    resolver: zodResolver(aktoerFormSchema),
    values: getDefaultAktoerFormValues(bestilling),
    disabled: readOnly,
    mode: "onSubmit",
  });
  const valgtAktorer = useFromToSelected(form.watch("aktoer"), mySelfSelect);
  const valgtAktoerType = form.watch("aktoer.type");

  const updateBestillingQuery = (data: aktoerFormValues) => {
    if (!data.aktoer) {
      return Promise.resolve();
    }
    const oppdatertAktoer: BestillingAktoer = {
      type: data.aktoer.type,
      organisasjonsnummer: Number(data.aktoer.organisasjonsnummer),
    };

    return Promise.resolve(
      setBestillingData({
        ...bestilling,
        aktoer: oppdatertAktoer,
      }),
    );
  };

  async function handleSubmit(data: aktoerFormValues) {
    if (isDirty(form.formState)) {
      await updateBestillingQuery(data);
    }
    await navigateNext();
  }

  const onAktoerSelectedChange = (item: AktoerSelected | null) => {
    form.clearErrors();
    const aktoer = convertSelctedToForm(item ?? undefined, mySelfSelect);
    form.setValue("aktoer", aktoer, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const selectId = useId();
  return (
    <section>
      <FormProvider {...form}>
        <FormNavigationBlocker form={form} onSubmit={updateBestillingQuery} />
        <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
          <Field>
            <Label htmlFor={selectId}>
              {t("pages.aktoer.form.felt_aktoer")}
            </Label>
            <Suggestion
              name="aktoer"
              selected={valgtAktorer}
              onSelectedChange={onAktoerSelectedChange}
              multiple={false}
            >
              <Suggestion.Input readOnly={readOnly} id={selectId} />
              {!readOnly && <Suggestion.Clear />}
              <Suggestion.List
                singular={t("pages.aktoer.singular")}
                plural={t("pages.aktoer.plural")}
              >
                {!aktoerer && (
                  <Suggestion.Empty>
                    <span>{t("pages.aktoer.ingen-treff")}</span>
                  </Suggestion.Empty>
                )}
                {mySelfSelect && (
                  <Suggestion.Option
                    key={mySelfSelect.value}
                    value={mySelfSelect.value}
                  >
                    {mySelfSelect.label}
                  </Suggestion.Option>
                )}
                {aktoerer?.map((aktoer) => (
                  <Suggestion.Option
                    key={aktoer.organisasjonsnummer}
                    value={aktoer.organisasjonsnummer}
                  >
                    {formatAktoerNavn(aktoer)}
                  </Suggestion.Option>
                ))}
              </Suggestion.List>
            </Suggestion>
            {form.formState.errors.aktoer && (
              <TranslatedValidationMessage
                error={form.formState.errors.aktoer}
              />
            )}
          </Field>

          {valgtAktoerType === "Selskap" && (
            <Field>
              <Label>{t("pages.aktoer.form.innsender")}</Label>
              <Select readOnly={true}>
                <Select.Option>{mySelfSelect.label}</Select.Option>
              </Select>
            </Field>
          )}

          <BestillingFormErrorSummary errors={form.formState.errors} />

          <BestillingNavigationActions
            currentPageKey={currentPageKey}
            disableNext={readOnly}
          />
        </FormWrapper>
      </FormProvider>
    </section>
  );
}
